import type { DataSourceId, SyncResult } from '../../contexts/DataSourceContext';

export const WELCOME_FIRST_SYNC_TIMEOUT_MS = 90_000;

export type WelcomeFirstSyncState =
  | 'sync_success'
  | 'zero_item_success'
  | 'source_not_connected'
  | 'token_validation_failed'
  | 'provider_failure'
  | 'store_not_ready'
  | 'building_missions'
  | 'blocked_timeout';

export interface WelcomeFirstSyncOutcome {
  state: WelcomeFirstSyncState;
  sourceId: DataSourceId;
  reasonCode: string;
  itemCount?: number;
}

export interface WelcomeFirstSyncRunnerInput {
  sourceId: DataSourceId;
  syncSource: (sourceId: DataSourceId) => Promise<SyncResult>;
  timeoutMs?: number;
  hasSourceWriteEvidence?: (sourceId: DataSourceId) => boolean;
}

function defaultHasSourceWriteEvidence(sourceId: DataSourceId): boolean {
  if (typeof window === 'undefined') return false;
  return window.__OWNYOU_MISSION_SPINE__?.markers.some(marker =>
    marker.hop === 'source_write' && marker.sourceId === sourceId && marker.status === 'success',
  ) ?? false;
}

function classifySyncFailure(sourceId: DataSourceId, error: unknown): WelcomeFirstSyncOutcome {
  const message = typeof error === 'string'
    ? error
    : error instanceof Error
      ? error.message
      : typeof (error as { error?: unknown } | null)?.error === 'string'
        ? String((error as { error?: string }).error)
        : 'provider failure';
  const normalized = message.toLowerCase();

  if (normalized.includes('not connected')) {
    return { sourceId, state: 'source_not_connected', reasonCode: 'source-not-connected' };
  }
  if (normalized.includes('store not ready')) {
    return { sourceId, state: 'store_not_ready', reasonCode: 'store-not-ready' };
  }
  if (normalized.includes('token') || normalized.includes('auth') || normalized.includes('reauth')) {
    return { sourceId, state: 'token_validation_failed', reasonCode: 'token-validation-failed' };
  }
  return { sourceId, state: 'provider_failure', reasonCode: 'provider-failure' };
}

export async function runWelcomeFirstSync(input: WelcomeFirstSyncRunnerInput): Promise<WelcomeFirstSyncOutcome> {
  const timeoutMs = input.timeoutMs ?? WELCOME_FIRST_SYNC_TIMEOUT_MS;
  const timeout = Symbol('welcome-first-sync-timeout');
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    const result = await Promise.race([
      input.syncSource(input.sourceId),
      new Promise<typeof timeout>(resolve => {
        timeoutId = setTimeout(() => resolve(timeout), timeoutMs);
      }),
    ]);

    if (result === timeout) {
      const hasSourceWrite = (input.hasSourceWriteEvidence ?? defaultHasSourceWriteEvidence)(input.sourceId);
      return {
        sourceId: input.sourceId,
        state: hasSourceWrite ? 'building_missions' : 'blocked_timeout',
        reasonCode: hasSourceWrite ? 'source-write-observed-building-missions' : 'first-sync-timeout-no-source-write',
      };
    }

    if (!result.success) {
      return classifySyncFailure(input.sourceId, result.error ?? 'provider failure');
    }

    return {
      sourceId: input.sourceId,
      state: result.itemCount > 0 ? 'sync_success' : 'zero_item_success',
      reasonCode: result.itemCount > 0 ? 'source-sync-success' : 'source-sync-zero-items',
      itemCount: result.itemCount,
    };
  } catch (error) {
    return classifySyncFailure(input.sourceId, error);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
